import { FC, useCallback } from 'react';
import { Box, Image } from '@chakra-ui/react';

import useSendDownloadSMS from 'common/hooks/useSendDownloadSMS';
import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import Page from 'features/Onboarding/legacy/components/Page';

import PageProps from '../PageProps';
import RaceFlag from './assets/RaceFlag@2x.png';
import locales from './locales';
import useAutoReturnToFalseState from './useAutoReturnToFalseState';

const RETRY_LOADING_TIME = 1000;
const RETRY_DISABLED_TIME = 3000;

const DownloadShineMessageSent: FC<PageProps> = () => {
  const { isSending, sendDownloadSMS, usedPhone } = useSendDownloadSMS(
    'Onboarding_App_Download',
  );
  // The download button should be disabled when the user first lands on the page under the
  // assumption that they have just pressed the send button before being sent here. At this first
  // load and after each time the button is pressed, it will be kept disabled for RETRY_DISABLED_TIME.

  // When the user does press the button, it will be kept in a loading state for at least
  // a minimum of RETRY_LOADING_TIME.
  const [isSendingBasedOnTimer, setIsSendingBasedOnTimerToTrue] =
    useAutoReturnToFalseState({ falsingDelay: RETRY_LOADING_TIME });
  const [isSendDisabled, setIsSendDisabledToTrue] = useAutoReturnToFalseState({
    falsingDelay: RETRY_DISABLED_TIME,
    startWithTrue: true,
  });

  const showIsSending = isSending || isSendingBasedOnTimer;

  const onSend = useCallback(() => {
    setIsSendingBasedOnTimerToTrue();
    setIsSendDisabledToTrue();
    sendDownloadSMS();
  }, [
    sendDownloadSMS,
    setIsSendDisabledToTrue,
    setIsSendingBasedOnTimerToTrue,
  ]);
  const buttonMessage = isSendDisabled ? locales.sending : locales.sendAgain;

  return (
    <Page backButtonNavigatesBack>
      <Box>
        <Image
          boxSize="180px"
          marginBottom="space-16"
          marginX="auto"
          src={RaceFlag}
        />

        <Heading marginBlockEnd="space-16" size="lg">
          {locales.thankYou}
        </Heading>

        <Text marginBottom="space-32" variant="primary">
          {locales.sentTo}{' '}
          <Text as="span" fontWeight="weight-500">
            {usedPhone}
          </Text>
        </Text>

        <Button
          isDisabled={isSendDisabled}
          isExpanded
          isLoading={showIsSending}
          onClick={onSend}
        >
          {buttonMessage}
        </Button>
      </Box>
    </Page>
  );
};

export default DownloadShineMessageSent;
